// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { portfolioMultiAssetApi } from '@shared/data-access/api/ai/portfolio-multiasset-api';
import { proposalsApi } from '@shared/data-access/api/brokerage/proposals-api';
import {
  selectClientId,
  selectClient,
  selectClientProductsAccounts,
} from '@shared/data-access/slices/clients';
import { getBenchmarkByRiskProfile } from '@shared/data-access/utils/risk-profile-benchmarks';
import type {
  InternalProposalOperation,
  InternalTargetPortfolioPosition,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import type { TargetAsset } from '@shared/data-access/interfaces/api/ai/target-strategy';
import type { PortfolioStrategy } from '@shared/data-access/interfaces/api/ai/portfolio-strategy';
import type { Holding } from '@shared/data-access/interfaces/api/ai/holding';
import { useAccountsPositions } from '@shared/advisory/aggregation/hooks/use-accounts-positions';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { type AdvisoryProposalResponseBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { isUndefined } from '@shared/utils';
import produce from 'immer';
import { isTargetPortfolioSimulationSuccessful } from '@shared/data-access/transformers/proposals/build-trader-target-portfolio';

/**
 * Hook to validate if portfolio volatility exceeds benchmark volatility.
 *
 * This hook performs a multi-asset portfolio analysis using the benchmark
 * corresponding to the client's risk profile and the target portfolio holdings. It
 * compares the portfolio volatility against the benchmark volatility.
 *
 * This hook calculates the target portfolio from scratch using the current client
 * positions and the proposed operations, since no proposal exists yet.
 *
 * @param operations - The proposed operations to be included in the proposal
 * @returns An object containing:
 *
 *   - `volatilityExceedsBenchmark`: boolean indicating if portfolio volatility exceeds
 *       benchmark
 *   - `isLoading`: boolean indicating if the analysis is still loading
 *   - `portfolioVolatility`: portfolio volatility value (if available)
 *   - `benchmarkVolatility`: benchmark volatility value (if available)
 */
export const useProposalVolatilityValidation = ({
  operations = [],
  advisory_proposal,
}: {
  operations?: InternalProposalOperation[];
  advisory_proposal?: AdvisoryProposalResponseBody | null;
}) => {
  const clientId = useSelector(selectClientId);
  const selectedClient = useSelector(selectClient);
  const productAccounts = useSelector(selectClientProductsAccounts);
  const currency = useSelector(selectAccountCurrency);
  const advisoryProposalAccounts = useMemo(
    () =>
      advisory_proposal?.institutions.flatMap(({ products, ...institution }) => {
        const advisoryInstitution = productAccounts.find(
          ({ institution_id }) => institution_id === institution.institution_id,
        )!;

        if (isUndefined(advisoryInstitution)) return [];

        return produce(advisoryInstitution, draft => {
          draft.accounts = draft.accounts.filter(
            ({ iban }) =>
              !!products.accounts.find(account => account.iban === iban),
          );
        });
      }) ?? [],
    [advisory_proposal?.institutions, productAccounts],
  );

  const ibans = useMemo(
    () =>
      advisoryProposalAccounts.flatMap(({ accounts }) =>
        accounts.map(({ iban }) => iban),
      ),
    [advisoryProposalAccounts],
  );

  const { accountsPositionsQuery, ...accountsPositions } = useAccountsPositions({
    ibans: ibans,
    clientId: clientId,
    skip:
      !clientId ||
      operations.length === 0 ||
      selectedClient?.risk_profile === null ||
      isUndefined(advisory_proposal),
    institutionId: 'all',
    currency,
  });

  const currentPortfolio = useMemo<InternalTargetPortfolioPosition[]>(() => {
    return accountsPositions.holdings
      .map(holding => ({
        iban: holding.iban!,
        ticker: holding?.details?.ticker ?? holding.ticker! ?? holding.isin ?? '',
        isin: holding?.details?.isin ?? holding.isin ?? '',
        quantity: holding.quantity,
        amount: holding.investment,
        institution_id: holding.institution_id!,
      }))
      .concat(
        advisoryProposalAccounts.flatMap(({ accounts, institution_id }) =>
          accounts
            .filter(({ cash }) => cash !== null && cash !== 0)
            .map(account => ({
              ticker: '$CASH',
              iban: account.iban,
              quantity: 1,
              amount: account.cash!,
              institution_id: institution_id,
              isin: '$CASH',
            })),
        ),
      );
  }, [accountsPositions.holdings, advisoryProposalAccounts]);

  const { data: simulation, ...targetPortfolioQuery } =
    proposalsApi.useCreateTargetPortfolioQuery(
      {
        current_portfolio: currentPortfolio,
        operations: operations,
      },
      {
        skip:
          !clientId ||
          operations.length === 0 ||
          accountsPositionsQuery.isLoading ||
          !accountsPositionsQuery.isSuccess ||
          isUndefined(advisory_proposal),
      },
    );

  const targetPortfolio = simulation?.positions ?? [];

  const benchmark = useMemo<TargetAsset[]>(() => {
    const riskProfile = selectedClient?.risk_profile;

    return getBenchmarkByRiskProfile(riskProfile);
  }, [selectedClient?.risk_profile]);

  const targetHoldings = useMemo<Holding[]>(() => {
    return targetPortfolio.map(target => ({
      ticker: target.ticker,
      unrealized_capital_gains: 0,
      price: target.price,
      qty: target.quantity,
    }));
  }, [targetPortfolio]);

  const portfolioMultiAssetArgs = useMemo<{
    params: { hist_w: number };
    payload: PortfolioStrategy;
  }>(
    () => ({
      params: {
        hist_w: 365,
      },
      payload: {
        benchmark: { target_asset: benchmark },
        holdings: targetHoldings,
      },
    }),
    [benchmark, targetHoldings],
  );

  const {
    isLoading: isLoadingAnalysis,
    isFetching: isFetchingAnalysis,
    data: analysis,
  } = portfolioMultiAssetApi.usePortfolioMultiAssetAnalysisQuery(
    portfolioMultiAssetArgs,
    {
      skip:
        !clientId ||
        operations.length === 0 ||
        accountsPositionsQuery.isLoading ||
        !accountsPositionsQuery.isSuccess ||
        targetPortfolioQuery.isLoading ||
        !targetPortfolioQuery.isSuccess ||
        benchmark.length === 0 ||
        targetHoldings.length === 0,
    },
  );

  const isLoading = useMemo(() => {
    return [
      accountsPositionsQuery.isLoading,
      accountsPositionsQuery.isFetching,
      targetPortfolioQuery.isLoading,
      targetPortfolioQuery.isFetching,
      isLoadingAnalysis,
      isFetchingAnalysis,
    ].some(Boolean);
  }, [
    accountsPositionsQuery.isLoading,
    accountsPositionsQuery.isFetching,
    targetPortfolioQuery.isLoading,
    targetPortfolioQuery.isFetching,
    isLoadingAnalysis,
    isFetchingAnalysis,
  ]);

  const volatilityExceedsBenchmark = useMemo(() => {
    if (!analysis?.risk) return false;

    const portfolioVolatility = analysis.risk.volatility;
    const benchmarkVolatility = analysis.risk.bnch_volatility;

    return portfolioVolatility > benchmarkVolatility;
  }, [analysis?.risk]);

  const portfolioVolatility = analysis?.risk?.volatility;
  const benchmarkVolatility = analysis?.risk?.bnch_volatility;

  const isSimulationSuccessful = useMemo(
    () =>
      targetPortfolioQuery.isSuccess &&
      isTargetPortfolioSimulationSuccessful(simulation),
    [simulation, targetPortfolioQuery.isSuccess],
  );

  return {
    volatilityExceedsBenchmark,
    isLoading,
    portfolioVolatility,
    benchmarkVolatility,
    benchmark,
    advisoryProposalAccounts,
    isSimulationSuccessful,
    simulationStatus: simulation?.status,
    operationResults: simulation?.operation_results ?? [],
  };
};
